import { validateTextField } from '@/utils/validateTextField';

import type {
  QuizLocale,
  V2QuizEditorQuestion,
  V2QuizEditorState,
} from './state';
import { oppositeLanguage } from './state';

const hasLocalizedText = (
  record: Record<string, string> | undefined,
  language: QuizLocale,
) => validateTextField(record?.[language] ?? '');

export const isTrueFalseQuestion = (question: V2QuizEditorQuestion) =>
  question.questionType === 'tf' ||
  (question.questionType !== 'mc' && question.answers.length === 2);

export const isQuestionComplete = (
  question: V2QuizEditorQuestion,
  language: QuizLocale,
) => {
  const other = oppositeLanguage(language);
  if (
    !hasLocalizedText(question.question, language) ||
    !hasLocalizedText(question.question, other)
  ) {
    return false;
  }

  if (!question.answers.some(answer => answer.correct === true)) {
    return false;
  }

  if (isTrueFalseQuestion(question)) {
    return true;
  }

  return question.answers.every(
    answer =>
      hasLocalizedText(answer.answer, language) &&
      hasLocalizedText(answer.answer, other),
  );
};

/**
 * Primary-language completeness only — used for leave "Are you sure?" gating.
 * Missing translations are flushable via applyAllTranslations, not discard-worthy.
 */
export const isQuestionPrimaryComplete = (
  question: V2QuizEditorQuestion,
  language: QuizLocale,
) => {
  if (!hasLocalizedText(question.question, language)) {
    return false;
  }

  if (!question.answers.some(answer => answer.correct === true)) {
    return false;
  }

  if (isTrueFalseQuestion(question)) {
    return true;
  }

  return question.answers.every(answer =>
    hasLocalizedText(answer.answer, language),
  );
};

export const isTitleComplete = (
  name: V2QuizEditorState['name'],
  language: QuizLocale,
) => {
  const other = oppositeLanguage(language);
  return hasLocalizedText(name, language) && hasLocalizedText(name, other);
};

export const getCompleteQuestions = (state: V2QuizEditorState) =>
  state.questions.filter(question =>
    isQuestionComplete(question, state.primaryLanguage),
  );

export const hasIncompleteQuestionWork = (state: V2QuizEditorState) =>
  state.questions.some(
    question => !isQuestionComplete(question, state.primaryLanguage),
  );

/** Incomplete authoring in the given locale — show leave confirm; translations alone should flush. */
export const hasIncompletePrimaryQuestionWork = (
  state: V2QuizEditorState,
  language: QuizLocale = state.primaryLanguage,
) =>
  state.questions.some(
    question => !isQuestionPrimaryComplete(question, language),
  );

/** Strip client-only fields before POST/PUT. */
export const toSavableQuestions = (
  questions: V2QuizEditorQuestion[],
): Omit<V2QuizEditorQuestion, 'clientId'>[] =>
  questions.map(({ clientId: _clientId, ...rest }) => rest);
